import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api/client";
import { handleEmploymentContract } from "../utils/parser/employmentContractParser";
import { handleExpenseDocument } from "../utils/parser/expenseParser";
import { handleIdentityDocument } from "../utils/parser/identityConfirmationParser";
import { handleIncomeDocument } from "../utils/parser/incomeParser";
import handleOutcomeParser from "../utils/parser/outcomeParser";
import type { LoanApplicationData, useNbiForm } from "./useNbiForm";

interface RegionDefaults {
    citizenship: string;
    state: string;
    country: string;
    currencyCode?: string;
}


interface UseApplicationRecoveryOptions {
    form: ReturnType<typeof useNbiForm>;
    regionDefaults?: RegionDefaults;
}

interface RecoveryState {
    isRecovering: boolean;
    isCreating: boolean;
    hasInitialized: boolean;
    error: string | null;
}

export const getAppIdFromUrl = (): number | undefined => {
    if (typeof window === "undefined") return undefined;
    const searchParams = new URLSearchParams(window.location.search);
    const appIdParam = searchParams.get("appId");
    return appIdParam ? Number(appIdParam) : undefined;
};

const hasStepInUrl = (): boolean => {
    if (typeof window === "undefined") return false;
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has("step");
};

export const useApplicationRecovery = ({ form, regionDefaults }: UseApplicationRecoveryOptions) => {
    const [state, setState] = useState<RecoveryState>({
        isRecovering: false,
        isCreating: false,
        hasInitialized: false,
        error: null,
    });

    const hasStartedInit = useRef(false);
    const formRef = useRef(form);
    formRef.current = form;
    const initialAppId = useRef(getAppIdFromUrl());
    const initialHasStep = useRef(hasStepInUrl());

    const createApplication = async () => {
        try {
            const result = await api.createApplication();
            const createdAppId = result.applicationId;
            // biome-ignore lint/suspicious/noExplicitAny: Form library requires type assertion
            formRef.current.setFieldValue("applicationId" as any, createdAppId);
            setState({ isRecovering: false, isCreating: false, hasInitialized: true, error: null });
        } catch (error) {
            setState({
                isRecovering: false,
                isCreating: false,
                hasInitialized: true,
                error: error instanceof Error ? error.message : "Failed to create application",
            });
        }
    };

    const refreshApplication = async (appId: number) => {
        setState({ isRecovering: true, isCreating: false, hasInitialized: false, error: null });
        try {
            const setFieldValue = (field: string, value: unknown) => formRef.current.setFieldValue(field as any, value);

            const result = await api.getApplicationStatus(appId);

            const allDocuments = Array.isArray(result.data?.documents) ? result.data?.documents || [] : [];
            const processingDocs = allDocuments.filter((d: any) => d.status === "processing");
            const settledDocs = allDocuments.filter((d: any) => d.status !== "processing");

            setFieldValue("documents", settledDocs);

            if (processingDocs.length > 0) {
                setFieldValue(
                    "uploadStates",
                    processingDocs.map((d: any) => ({
                        status: "processing",
                        progress: 0,
                        fileName: d.fileName,
                        documentId: d.id,
                        size: d.size,
                        uploadedAt: Date.now(),
                    }))
                );
            }

            setFieldValue("applicationId", appId);
            const applicants = Array.isArray(result.data?.applicants) ? result.data?.applicants : [];

            applicants.forEach((applicant) => {
                handleIdentityDocument(applicant, formRef.current.state?.values as LoanApplicationData, setFieldValue, regionDefaults);
            });

            const employments = Array.isArray(result.data?.employments) ? result.data?.employments : [];
            employments.forEach((employment) => {
                handleEmploymentContract(
                    employment,
                    formRef.current.state?.values as LoanApplicationData,
                    setFieldValue,
                    employment.applicantId != null ? String(employment.applicantId) : null
                );
            });

            const incomes = Array.isArray(result.data?.incomes) ? result.data?.incomes : [];
            incomes.forEach((income) => {
                handleIncomeDocument(
                    income,
                    formRef.current.state?.values as LoanApplicationData,
                    setFieldValue,
                    income.applicantId != null ? String(income.applicantId) : null,
                    income.employmentId != null ? String(income.employmentId) : null,
                    regionDefaults?.currencyCode
                );
            });

            const expenses = Array.isArray(result.data?.expenses) ? result.data?.expenses : [];
            expenses.forEach((expense) => {
                handleExpenseDocument(
                    expense,
                    formRef.current.state?.values as LoanApplicationData,
                    setFieldValue
                );
            });

            const { propertyMarketValue, loanAmount, downPayment } = result.data.amounts;
            const downPaymentPercentage = propertyMarketValue === 0
                ? (downPayment > 0 ? 100 : 0)
                : (downPayment / propertyMarketValue) * 100;
            setFieldValue("amounts", { propertyMarketValue, loanAmount, downPayment, downPaymentPercentage });

            if (result.outcomeStatus === "completed" && result.data?.assessmentResult) {
                handleOutcomeParser(appId, result.userId, result.data?.assessmentResult, setFieldValue);
                setFieldValue("submitted", true);
            }

            setState({ isRecovering: false, isCreating: false, hasInitialized: true, error: null });
        } catch (error) {
            console.error("Error recovering application:", error);
            setState({
                isRecovering: false,
                isCreating: false,
                hasInitialized: true,
                error: error instanceof Error ? error.message : "Failed to recover application",
            });
        }
    };

    const initApplication = async () => {
        if (hasStartedInit.current) return;
        hasStartedInit.current = true;
        const appId = initialAppId.current;
        const hasApplicationID = appId !== undefined && appId !== null;
        if (hasApplicationID) {
            refreshApplication(appId);
        } else {
            const hasStep = initialHasStep.current;
            if (!hasStep) {
                setState({ isRecovering: false, isCreating: false, hasInitialized: true, error: null });
                return;
            }
            setState({ isRecovering: false, isCreating: true, hasInitialized: false, error: null });
            createApplication();
        }
    };

    useEffect(() => {
        initApplication();
    }, []);

    return { ...state, refreshApplication };
};
